import { FC } from 'react';

import cn from 'classnames';
import { Button, Input, Spinner } from 'components';
import { Field, FieldProps, Form, Formik } from 'formik';

import { ErrorStub, StubNotFound } from './components';
import { PaymentInfo } from './Payment.entity';
import { validationPayment } from './Payment.helpers';
import styles from './PaymentPage.module.css';

type Props = {
  status: 'success' | 'error';
  onSubmit: (values: { email: string }) => void;
  isAcquiringForbidden: boolean;
  email: string;
  onClickRetry: () => void;
  paymentInfo: PaymentInfo | undefined;
  isLoadingInfo: boolean;
  isLoadingPayment: boolean;
  isParamsValid: boolean;
  isInfoError: boolean;
  isPaymentError: boolean;
  paramsError: string;
  infoError: string;
  paymentError: string;
  infoTraceId: string | undefined;
  paymentTraceId: string | undefined;
};

export const PaymentPage: FC<Props> = ({
  onSubmit,
  isAcquiringForbidden,
  email,
  onClickRetry,
  paymentInfo,
  isLoadingInfo,
  isLoadingPayment,
  isParamsValid,
  isInfoError,
  isPaymentError,
  paramsError,
  infoError,
  paymentError,
  infoTraceId,
  paymentTraceId,
}) => {
  if (!isParamsValid) {
    return (
      <div className={styles.root}>
        <div className={cn(styles.container, styles.stub)}>
          <StubNotFound
            title="Платеж не сформирован"
            description={`К сожалению, мы не смогли создать платеж.  Причина: "${paramsError}". ` +
              'Если есть вопросы – напишите нам.'}
          />
        </div>
      </div>
    );
  }

  if ((isInfoError && !isLoadingInfo) || (isPaymentError && !isLoadingPayment)) {
    const isInfo = isInfoError && !isLoadingInfo;
    const errorText = isInfo ? infoError : paymentError;
    const traceId = isInfo ? infoTraceId : paymentTraceId;
    return (
      <div className={styles.root}>
        <div className={cn(styles.container, styles.stub)}>
          <StubNotFound
            title="Платеж не сформирован"
            description={
              infoTraceId
                ? `К сожалению, мы не смогли создать платеж.  Причина: "${errorText}". ` +
                `Если есть вопросы – напишите нам и сообщите код ${traceId}`
                : 'К сожалению, мы не смогли создать платеж. Если есть вопросы – напишите нам.'
            }
          />
        </div>
      </div>
    );
  }

  if (isLoadingInfo) {
    return (
      <div className={styles.root}>
        <Spinner />
      </div>
    );
  }

  if (!paymentInfo) {
    return (
      <div className={styles.root}>
        <div className={cn(styles.container, styles.stub)}>
          <ErrorStub
            title="Произошла ошибка"
            description="Произошла ошибка, попробуйте позже"
            onClickRetry={onClickRetry}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Formik
        initialValues={{ email }}
        onSubmit={onSubmit}
        validationSchema={validationPayment}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {() => (
          <Form className={styles.form}>
            <div className={styles.container}>
              <div className={styles.logo}>
                <img src="/img/logo.png" width={88} />
              </div>
              <div className={styles.blockMainInfo}>
                <div className={styles.transactionNumber}>{paymentInfo.title}</div>
                <div className={styles.paymentAmount}>
                  <div className={styles.amount}>
                    {paymentInfo.toPaySum.toLocaleString('ru', {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                  <div className={styles.symbol}>₽</div>
                </div>
                <div className={styles.descriptions}>{paymentInfo.description}</div>
              </div>
              {!isAcquiringForbidden && (
                <div className={styles.emailBlock}>
                  <Field name="email">
                    {({ field: { name, value }, form: { setFieldValue, errors } }: FieldProps) => (
                      <Input
                        label="Электронная почта для чека"
                        value={value}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/\s+/g, '');
                          void setFieldValue(name, cleaned);
                        }}
                        placeholder="Например, name@domain.ru"
                        errorText={errors[name] ? String(errors[name]) : ''}
                        inputProps={{
                          type: 'email',
                          spellCheck: false,
                          autoComplete: 'email',
                        }}
                      />
                    )}
                  </Field>
                </div>
              )}
              {!isAcquiringForbidden && (
                <div className={styles.footer}>
                  <Button type="submit" isLoading={isLoadingPayment}>
                    Оплатить
                  </Button>

                  <div className={styles.politic}>
                    {'Нажимая кнопку «Оплатить», я соглашаюсь с условиями '}
                    <a target="_blank" href="/user-agreement">
                      пользовательского соглашения
                    </a>{' '}
                    и{' '}
                    <a target="_blank" href="/policy">
                      политики конфиденциальности
                    </a>
                  </div>
                  <div className={styles.paymentSystems}>
                    <img src="/img/payment_systems.png" width={165} />
                  </div>
                </div>
              )}
              {isAcquiringForbidden && (
                <div className={styles.informer}>Возможна оплата только по QR коду из квитанции.</div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
